/**
 * FourStepWizardForm_v01.tsx
 * React + TypeScript + React-Bootstrap
 *
 * A 4-step wizard with a vertical (all-steps) view. Each step's UI is created once
 * and reused in both layouts via const renderers. Modular, readable, and documented.
 *
 * Design goals:
 * - Single source of truth for form state.
 * - Minimal but sensible validation on critical fields.
 * - Reusable step renderers for Wizard and Vertical layouts.
 * - Clear separation of concerns (state, validators, UI, submit).
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Nav,
  Navbar,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";

// ---------- Types ----------

type LegalStructure = "LLC" | "S-Corp" | "C-Corp" | "Sole Prop" | "Partnership" | "Other";

interface AppModel {
  // Step 1: Contact & Address
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;

  // Step 2: Business Info
  businessName: string;
  legalStructure: LegalStructure | "";
  yearsInBusiness: string; // keep as string for wire payloads
  numEmployees: string;
  numContractors: string;
  annualRevenue: string;
  primaryServices: string[]; // checkbox/group
  website: string;
  ein: string;

  // Step 3: Notes
  notes: string;
}

interface SubmitState {
  loading: boolean;
  success?: boolean;
  error?: string;
}

// ---------- Utilities (validators, helpers) ----------

const required = (s: string) => s.trim().length > 0;
const validEmail = (s: string) => /^\S+@\S+\.\S+$/.test(s.trim());
const digitsOnly = (s: string) => s.replace(/\D/g, "");
const pretty = (s: string | string[]) =>
  Array.isArray(s) ? s.join(", ") || "—" : (s?.trim() || "—");

const initialApp: AppModel = {
  name: "",
  phone: "",
  email: "",
  address: "",
  city: "",
  state: "",
  zip: "",

  businessName: "",
  legalStructure: "",
  yearsInBusiness: "",
  numEmployees: "",
  numContractors: "",
  annualRevenue: "",
  primaryServices: [],
  website: "",
  ein: "",

  notes: "",
};

const SERVICE_OPTIONS = [
  "Residential work",
  "Commercial work",
  "New construction",
  "Remodel / retrofit",
  "Emergency / after-hours",
  "Consulting / inspections",
];

// The minimal fields required to advance past Step 1:
const mustHaveStep1 = (app: AppModel) =>
  required(app.name) &&
  required(app.phone) &&
  validEmail(app.email) &&
  required(app.address) &&
  required(app.city) &&
  required(app.state) &&
  required(app.zip);

// ---------- Component ----------

export const FourStepWizardForm_v01: React.FC = () => {
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const [activeStep, setActiveStep] = useState(0); // 0..3
  const [app, setApp] = useState<AppModel>({ ...initialApp });
  const [submit, setSubmit] = useState<SubmitState>({ loading: false });
  const [showValidated, setShowValidated] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);

  // Unified change handlers
  const updateField = (field: keyof AppModel) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.currentTarget.value;
    setApp((prev) => ({ ...prev, [field]: value }));
  };

  const updateSelect = (field: keyof AppModel) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setApp((prev) => ({ ...prev, [field]: e.currentTarget.value }));
  };

  const toggleService = (svc: string) => {
    setApp((prev) => {
      const has = prev.primaryServices.includes(svc);
      return {
        ...prev,
        primaryServices: has
          ? prev.primaryServices.filter((s) => s !== svc)
          : [...prev.primaryServices, svc],
      };
    });
  };

  // Derived UI bits
  const stepLabels = ["Contact", "Business", "Notes", "Confirm"];
  const progress = Math.round(((activeStep + 1) / stepLabels.length) * 100);

  // Navigation
  const goNext = () => {
    if (mode !== "wizard") return;
    // Gate Step 1 before advancing
    if (activeStep === 0 && !mustHaveStep1(app)) {
      setShowValidated(true);
      scrollToFormTop();
      return;
    }
    setActiveStep((s) => Math.min(s + 1, 3));
    scrollToFormTop();
  };

  const goPrev = () => {
    if (mode !== "wizard") return;
    setActiveStep((s) => Math.max(s - 1, 0));
    scrollToFormTop();
  };

  const goTo = (idx: number) => {
    if (mode !== "wizard") return;
    // Prevent skipping ahead of Step 1 requirements
    if (idx > 0 && !mustHaveStep1(app)) {
      setShowValidated(true);
      scrollToFormTop();
      return;
    }
    setActiveStep(idx);
    scrollToFormTop();
  };

  const scrollToFormTop = () => {
    if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Submit
  const handleSubmit = async () => {
    // Final guard: require Step 1 validity before submit as well
    if (!mustHaveStep1(app)) {
      setShowValidated(true);
      if (mode === "wizard") setActiveStep(0);
      scrollToFormTop();
      return;
    }

    setSubmit({ loading: true });
    try {
      // Simulate network call
      await new Promise((res) => setTimeout(res, 900));
      // Here you could POST `app` to your API as strings (already strings)
      setSubmit({ loading: false, success: true });
      // Optional: reset form
      // setApp({ ...initialApp });
    } catch (err: any) {
      setSubmit({ loading: false, error: err?.message || "Submission failed" });
    } finally {
      scrollToFormTop();
    }
  };

  // ---------- Step Renderers (created once; reused in Wizard and Vertical) ----------

  const Step1_Contact = useMemo(
    () => (
      <Card className="mb-3">
        <Card.Header as="h5">Step 1 — Contact & Address</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="name">
                <Form.Label>Full Name<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={app.name}
                  onChange={updateField("name")}
                  isInvalid={showValidated && !required(app.name)}
                  placeholder="Jane Doe"
                />
                <Form.Control.Feedback type="invalid">Name is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>Phone<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="tel"
                  value={app.phone}
                  onChange={(e) => {
                    // optional auto-clean to digits on blur could be added
                    setApp((p) => ({ ...p, phone: e.currentTarget.value }));
                  }}
                  isInvalid={showValidated && !required(app.phone)}
                  placeholder="(555) 123-4567"
                />
                <Form.Control.Feedback type="invalid">Phone is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>Email<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  value={app.email}
                  onChange={updateField("email")}
                  isInvalid={showValidated && !validEmail(app.email)}
                  placeholder="you@example.com"
                />
                <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="address">
                <Form.Label>Street Address<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={app.address}
                  onChange={updateField("address")}
                  isInvalid={showValidated && !required(app.address)}
                  placeholder="123 Main St"
                />
                <Form.Control.Feedback type="invalid">Address is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Group controlId="city">
                <Form.Label>City<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={app.city}
                  onChange={updateField("city")}
                  isInvalid={showValidated && !required(app.city)}
                />
                <Form.Control.Feedback type="invalid">City is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="state">
                <Form.Label>State<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={app.state}
                  onChange={updateField("state")}
                  isInvalid={showValidated && !required(app.state)}
                  placeholder="CO"
                />
                <Form.Control.Feedback type="invalid">State is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="zip">
                <Form.Label>ZIP<span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={app.zip}
                  onChange={(e) => {
                    const cleaned = digitsOnly(e.currentTarget.value).slice(0, 10);
                    setApp((p) => ({ ...p, zip: cleaned }));
                  }}
                  isInvalid={showValidated && !required(app.zip)}
                  placeholder="80202"
                />
                <Form.Control.Feedback type="invalid">ZIP is required.</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    ),
    [app, showValidated]
  );

  const Step2_Business = useMemo(
    () => (
      <Card className="mb-3">
        <Card.Header as="h5">Step 2 — Business Information</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="businessName">
                <Form.Label>Business Name</Form.Label>
                <Form.Control
                  type="text"
                  value={app.businessName}
                  onChange={updateField("businessName")}
                  placeholder="Acme Services LLC"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="legalStructure">
                <Form.Label>Legal Structure</Form.Label>
                <Form.Select
                  value={app.legalStructure || ""}
                  onChange={updateSelect("legalStructure")}
                >
                  <option value="">Select...</option>
                  <option value="LLC">LLC</option>
                  <option value="S-Corp">S-Corp</option>
                  <option value="C-Corp">C-Corp</option>
                  <option value="Sole Prop">Sole Proprietorship</option>
                  <option value="Partnership">Partnership</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={4}>
              <Form.Group controlId="yearsInBusiness">
                <Form.Label>Years in Business</Form.Label>
                <Form.Control
                  type="text"
                  value={app.yearsInBusiness}
                  onChange={(e) => {
                    const cleaned = digitsOnly(e.currentTarget.value).slice(0, 3);
                    setApp((p) => ({ ...p, yearsInBusiness: cleaned }));
                  }}
                  placeholder="5"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="numEmployees">
                <Form.Label>Employees</Form.Label>
                <Form.Control
                  type="text"
                  value={app.numEmployees}
                  onChange={(e) => {
                    const cleaned = digitsOnly(e.currentTarget.value).slice(0, 5);
                    setApp((p) => ({ ...p, numEmployees: cleaned }));
                  }}
                  placeholder="3"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="numContractors">
                <Form.Label>Contractors</Form.Label>
                <Form.Control
                  type="text"
                  value={app.numContractors}
                  onChange={(e) => {
                    const cleaned = digitsOnly(e.currentTarget.value).slice(0, 5);
                    setApp((p) => ({ ...p, numContractors: cleaned }));
                  }}
                  placeholder="2"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group controlId="annualRevenue">
                <Form.Label>Annual Revenue (USD)</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="text"
                    value={app.annualRevenue}
                    onChange={(e) => {
                      const cleaned = digitsOnly(e.currentTarget.value).slice(0, 12);
                      setApp((p) => ({ ...p, annualRevenue: cleaned }));
                    }}
                    placeholder="250000"
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="website">
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  value={app.website}
                  onChange={updateField("website")}
                  placeholder="https://example.com"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Form.Group controlId="ein">
                <Form.Label>EIN (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={app.ein}
                  onChange={updateField("ein")}
                  placeholder="12-3456789"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Primary Services</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((svc) => (
                    <Form.Check
                      key={svc}
                      type="checkbox"
                      id={`svc-${svc}`}
                      label={svc}
                      checked={app.primaryServices.includes(svc)}
                      onChange={() => toggleService(svc)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    ),
    [app]
  );

  const Step3_Notes = useMemo(
    () => (
      <Card className="mb-3">
        <Card.Header as="h5">Step 3 — Notes</Card.Header>
        <Card.Body>
          <Form.Group controlId="notes">
            <Form.Label>Additional Notes / Job Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={app.notes}
              onChange={updateField("notes")}
              placeholder="Tell us about project types, timelines, special requirements, etc."
            />
          </Form.Group>
        </Card.Body>
      </Card>
    ),
    [app]
  );

  const Step4_Confirm = useMemo(
    () => (
      <Card className="mb-3">
        <Card.Header as="h5">Step 4 — Confirm & Submit</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Section title="Contact">
                <KV label="Name" value={pretty(app.name)} />
                <KV label="Phone" value={pretty(app.phone)} />
                <KV label="Email" value={pretty(app.email)} />
              </Section>
            </Col>
            <Col md={6}>
              <Section title="Address">
                <KV label="Address" value={pretty(app.address)} />
                <KV label="City" value={pretty(app.city)} />
                <KV label="State" value={pretty(app.state)} />
                <KV label="ZIP" value={pretty(app.zip)} />
              </Section>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col md={6}>
              <Section title="Business">
                <KV label="Business Name" value={pretty(app.businessName)} />
                <KV label="Structure" value={pretty(app.legalStructure || "")} />
                <KV label="Years" value={pretty(app.yearsInBusiness)} />
                <KV label="Employees" value={pretty(app.numEmployees)} />
                <KV label="Contractors" value={pretty(app.numContractors)} />
                <KV label="Revenue (USD)" value={pretty(app.annualRevenue)} />
              </Section>
            </Col>
            <Col md={6}>
              <Section title="More">
                <KV label="Website" value={pretty(app.website)} />
                <KV label="EIN" value={pretty(app.ein)} />
                <KV label="Primary Services" value={pretty(app.primaryServices)} />
              </Section>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col>
              <Section title="Notes">
                <div className="text-muted">{pretty(app.notes)}</div>
              </Section>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Button variant="primary" onClick={handleSubmit} disabled={submit.loading}>
              {submit.loading ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" /> Submitting…
                </>
              ) : (
                "Submit"
              )}
            </Button>
            <Button variant="outline-secondary" onClick={() => setActiveStep(0)}>
              Edit from Step 1
            </Button>
          </div>

          {submit.success && (
            <Alert variant="success" className="mt-3">
              <strong>Success!</strong> Your information has been submitted.
            </Alert>
          )}
          {submit.error && (
            <Alert variant="danger" className="mt-3">
              <strong>Oops.</strong> {submit.error}
            </Alert>
          )}
        </Card.Body>
      </Card>
    ),
    [app, submit.loading, submit.success, submit.error]
  );

  // ---------- Layouts: Wizard & Vertical (reuse the same Step renderers) ----------

  const WizardLayout = (
    <>
      <Card className="mb-3">
        <Card.Body className="py-3">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex gap-2 align-items-center">
              {stepLabels.map((label, idx) => (
                <Button
                  key={label}
                  size="sm"
                  variant={idx === activeStep ? "primary" : "outline-secondary"}
                  onClick={() => goTo(idx)}
                >
                  {idx + 1}. {label}
                </Button>
              ))}
            </div>
            <div style={{ minWidth: 180 }}>
              <ProgressBar now={progress} label={`${progress}%`} aria-label="Wizard progress" />
            </div>
          </div>
        </Card.Body>
      </Card>

      {activeStep === 0 && Step1_Contact}
      {activeStep === 1 && Step2_Business}
      {activeStep === 2 && Step3_Notes}
      {activeStep === 3 && Step4_Confirm}

      <div className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={goPrev} disabled={activeStep === 0}>
          ← Previous
        </Button>
        {activeStep < 3 ? (
          <Button variant="primary" onClick={goNext}>
            Next →
          </Button>
        ) : (
          <Button variant="success" onClick={handleSubmit} disabled={submit.loading}>
            {submit.loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" /> Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        )}
      </div>
    </>
  );

  const VerticalLayout = (
    <>
      <Alert variant="info">
        <strong>All Steps View:</strong> This shows the entire form at once. The step sections below
        reuse the exact same UI components as the wizard.
      </Alert>
      {Step1_Contact}
      {Step2_Business}
      {Step3_Notes}
      {Step4_Confirm}
    </>
  );

  // ---------- Render ----------

  return (
    <Container className="py-4" ref={formRef}>
      <Navbar bg="light" expand="md" className="rounded mb-3 px-3">
        <Navbar.Brand className="fw-bold">4-Step Wizard (Reusable Steps)</Navbar.Brand>
        <Nav className="ms-auto">
          <Nav.Item>
            <Button
              size="sm"
              variant={mode === "wizard" ? "primary" : "outline-primary"}
              className="me-2"
              onClick={() => setMode("wizard")}
            >
              Wizard
            </Button>
          </Nav.Item>
          <Nav.Item>
            <Button
              size="sm"
              variant={mode === "vertical" ? "primary" : "outline-primary"}
              onClick={() => setMode("vertical")}
            >
              Vertical
            </Button>
          </Nav.Item>
        </Nav>
      </Navbar>

      <div className="mb-3">
        <Badge bg="secondary" pill className="me-2">
          Mode: {mode}
        </Badge>
        <Badge bg="info" pill>
          Steps reused in both layouts
        </Badge>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (mode === "wizard" && activeStep < 3) {
            goNext();
          } else {
            handleSubmit();
          }
        }}
      >
        {mode === "wizard" ? WizardLayout : VerticalLayout}
      </Form>
    </Container>
  );
};

// ---------- Small presentational subcomponents ----------

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card className="mb-2 shadow-sm">
    <Card.Header className="fw-semibold">{title}</Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);

const KV: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center border-bottom py-1">
    <span className="text-muted">{label}</span>
    <span className="ms-3">{value}</span>
  </div>
);

export default FourStepWizardForm_v01;
